import React, { useContext, useEffect, useState } from 'react';
import { SectionInstance } from '../types';
import InlineEditText from '../InlineEditText';
import { InlineEditContext } from '../InlineEditContext';
import { sfHeadline } from './sectionHelpers';

interface QuizOption {
  id: string;
  title: string;
}

const getApiBase = () => {
  const envUrl = process.env.REACT_APP_API_URL;
  if (envUrl && envUrl.startsWith('/')) {
    return `${window.location.origin}${envUrl}`;
  }
  return envUrl || `${window.location.protocol}//${window.location.hostname}:8001/api`;
};

const QuizEmbed: React.FC<{ section: SectionInstance }> = ({ section }) => {
  const c = section.controls;
  const ctx = useContext(InlineEditContext);
  const isEditMode = ctx?.isEditMode ?? false;

  const [quizzes, setQuizzes] = useState<QuizOption[]>([]);
  const [loadingQuizzes, setLoadingQuizzes] = useState(false);
  const [quizzesFetched, setQuizzesFetched] = useState(false);

  const projectId = ctx?.projectId || '';

  // Fetch available quizzes when section is in edit mode
  useEffect(() => {
    if (!isEditMode || quizzesFetched) return;

    const token = localStorage.getItem('access_token');
    const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
    const qs = projectId ? `?project_id=${encodeURIComponent(projectId)}` : '/';

    setLoadingQuizzes(true);
    fetch(`${getApiBase()}/quizzes${qs}`, { headers })
      .then(res => res.ok ? res.json() : [])
      .then((data: any[]) => {
        if (Array.isArray(data)) {
          setQuizzes(data.map(q => ({ id: String(q.quiz_id || q.id || ''), title: q.title || q.name || 'Untitled Quiz' })));
        }
        setQuizzesFetched(true);
      })
      .catch(() => setQuizzesFetched(true))
      .finally(() => setLoadingQuizzes(false));
  }, [isEditMode, quizzesFetched]);

  const handleQuizSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (!ctx) return;
    ctx.onControlChange(ctx.sectionId, 'quiz_id', e.target.value);
  };

  return (
    <section style={{ padding: '80px 24px' }}>
      <div style={{ maxWidth: 768, margin: '0 auto' }}>
        <InlineEditText
          tag="h2"
          controlKey="headline"
          style={{ textAlign: 'center', marginBottom: 24 }}
        >
          {sfHeadline(c, 'Take Our Quiz')}
        </InlineEditText>

        {/* Quiz picker — shown in edit mode when a quiz hasn't been selected yet or to change selection */}
        {isEditMode && (
          <div style={{
            marginBottom: 20,
            padding: '12px 16px',
            background: 'rgba(33, 119, 255, 0.06)',
            border: '1px solid rgba(33, 119, 255, 0.18)',
            borderRadius: 8,
          }}>
            <label style={{
              display: 'block',
              fontSize: '0.8rem',
              fontWeight: 600,
              color: '#2177FF',
              marginBottom: 8,
            }}>
              Select Quiz
            </label>
            {loadingQuizzes ? (
              <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>Loading quizzes...</p>
            ) : quizzes.length > 0 ? (
              <select
                value={c.quiz_id || ''}
                onChange={handleQuizSelect}
                onClick={e => e.stopPropagation()}
                style={{
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid rgba(0,0,0,0.15)',
                  borderRadius: 6,
                  fontSize: '0.875rem',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                <option value="">— Choose a quiz —</option>
                {quizzes.map(q => (
                  <option key={q.id} value={q.id}>{q.title}</option>
                ))}
              </select>
            ) : (
              <p style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                No quizzes found. Create one in the Quizzes module first.
              </p>
            )}
          </div>
        )}

        <div style={{
          border: c.show_border !== false ? '1px solid rgba(0,0,0,0.08)' : 'none',
          borderRadius: 12,
          padding: 40,
          textAlign: 'center',
          background: '#f8fafc',
          color: '#94a3b8',
        }}>
          {c.quiz_id
            ? `Quiz Widget (ID: ${c.quiz_id})`
            : 'Quiz Widget — select a quiz above to embed it'}
        </div>
      </div>
    </section>
  );
};

export default QuizEmbed;
